'use client';

import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { NormalizedCollection } from '@ravago/shared/page-data/models/contentful-mapped';
import { ProductCard as ProductCardModel } from '@ravago/shared/page-data/models/elements';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { InputGroup } from '@ravago/shared/radiance/components/input-field/components/InputGroup/InputGroup';
import { Pagination } from '@ravago/shared/radiance/components/pagination/components/Pagination/Pagination';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import { useTranslations } from '@ravago/shared/translations/client';

import debounce from 'lodash/debounce';

import { AppUtils } from '../../../../utils/app/app.utils';
import { ProductCard } from '../../../shared/cards/product-card/product-card';
import { EmptyState } from '../../../shared/empty-state/empty-state.component';
import { ProductCompareSelection } from '../../product-compare/components/product-compare-selection';
import { ProductListViewConfig } from '../product-list-functions';

interface Props {
   config: ProductListViewConfig;
   locale: string;
   isConsumer: boolean;
   contentType: string | undefined;
   brandName: string;
   assetBasePath: string;
   acceptWebp?: boolean;
   baseRoute?: string;
   products: NormalizedCollection<ProductCardModel>;
   hasPublicLabelAndSigningsPermission: boolean;
   hasSustainabilityLabels: boolean;
   paginatedProducts?: (page: number, searchTerm: string) => Promise<NormalizedCollection<ProductCardModel>>;
   hasProductCompareFeatureToggle: boolean;
}

/* c8 ignore star t */
export const ProductList: FC<Props> = ({
   config,
   locale,
   isConsumer,
   contentType,
   brandName,
   assetBasePath,
   acceptWebp,
   baseRoute,
   products,
   hasPublicLabelAndSigningsPermission,
   hasSustainabilityLabels,
   paginatedProducts,
   hasProductCompareFeatureToggle
}) => {
   const translate = useTranslations();
   const { phone } = useBreakpoint();
   const [productItems, setProductItems] = useState<NormalizedCollection<ProductCardModel>>({ total: 0, skip: 0, limit: 0, items: [] });
   const [searchTerm, setSearchTerm] = useState(config.initialSearchTerm);
   const [currentPage, setCurrentPage] = useState(config.initialPage);

   useEffect(() => setProductItems(products), [products]);

   const canShowPagination = config.showPagination && productItems.total > 0;

   const totalPages = canShowPagination ? Math.ceil(productItems.total / config.pageSize) : 0;

   // eslint-disable -next-line react-hooks/exhaustive-deps
   const onSearch = useCallback(
      debounce((value: string) => {
         if (searchTerm === '' || value === '') setCurrentPage(1);
         setSearchTerm(value);
      }, 300),
      []
   );

   const productCards = useMemo(() => productItems.items, [productItems]);

   useEffect(() => {
      if (paginatedProducts) {
         paginatedProducts(currentPage, searchTerm).then((response) => setProductItems(response));
      }
   }, [currentPage, searchTerm]);

   return (
      <>
         {config.showSearch && (
            <Box items="center" attributes={{ 'test-selector': AppUtils.getTestSelector('search', config.nodeType, config.testId) }}>
               <BoxItem grow={1}>
                  <InputGroup
                     id="product-document-search"
                     actionIcon="search"
                     width="100%"
                     clearable
                     onChange={onSearch}
                     label={translate('brands-search-placeholder')}
                  />
               </BoxItem>
            </Box>
         )}
         {config.showTotal && (
            <Typography variant="caption">
               {translate('brands-detail-total', { total: productItems?.total.toString() ?? '0', brand: brandName })}
            </Typography>
         )}
         {hasProductCompareFeatureToggle && <ProductCompareSelection locale={locale} />}
         <Grid useContainerQuery>
            {productCards?.map((product, productIndex) => (
               <GridCol key={product.id} useContainerQuery spanDesktop={3} spanTablet={6} spanPhone={4}>
                  <ProductCard
                     nodeType={config.nodeType}
                     testId={config.testId}
                     product={product}
                     locale={locale}
                     isConsumer={isConsumer}
                     hasPublicLabelAndSigningsPermission={hasPublicLabelAndSigningsPermission}
                     hasSustainabilityLabels={hasSustainabilityLabels}
                     hasProductCompareFeatureToggle={hasProductCompareFeatureToggle}
                     acceptWebp={acceptWebp}
                     assetBasePath={assetBasePath}
                     baseRoute={baseRoute}
                     useStandardCardBanner={contentType === 'caseStudy'}
                     deferImage={phone ? productIndex > 1 : productIndex > 5}
                  />
               </GridCol>
            ))}
         </Grid>
         {config.showSearch && productItems.total === 0 && <EmptyState icon="search" label={translate('products-no-search-results', { searchTerm })} />}
         {canShowPagination && (
            <Box justify="end" attributes={{ 'test-selector': AppUtils.getTestSelector('pagination', config.nodeType, config.testId) }}>
               <BoxItem>{totalPages > 0 && <Pagination total={totalPages} current={currentPage} onChange={setCurrentPage} />}</BoxItem>
            </Box>
         )}
      </>
   );
};
/* c8 ignore end */
